/**
 * Stock-summary React hook (INV-FG-1-1 aggregate read-model).
 *
 * TanStack Query binding for the per-product aggregate view backed by
 * `GET /inventory/stock-summaries`. Mirrors the `use-procurement-requests`
 * pattern (useQuery + queryKey + staleTime).
 */

import {
  getStockSummaries,
  type FourFieldStockSummary,
  type ProductGroupCode,
} from '@/api/inventory';
import { useQuery } from '@tanstack/react-query';

const SUMMARY_STALE_MS = 30_000;

export interface StockSummaryFilter {
  warehouseId?: string;
  groupCode?: ProductGroupCode;
}

export function useStockSummaries(filter: StockSummaryFilter = {}) {
  const query = useQuery({
    queryKey: ['stock-summaries', filter],
    queryFn: async () => {
      const res = await getStockSummaries(filter.warehouseId, filter.groupCode);
      return res.data;
    },
    staleTime: SUMMARY_STALE_MS,
  });

  return {
    summaries: (query.data ?? []) as FourFieldStockSummary[],
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    refetch: query.refetch,
  };
}
